import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserPlusIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserGroupIcon,
  AcademicCapIcon,
  CameraIcon,
  XMarkIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../api/client';
import { FireLoader } from '../components/Loader';
import {
  showConfirm,
  showSuccess,
  showError,
  showWarning,
  showInfo,
} from '../utils/alert';
import { useAuth } from '../context/AuthContext';
import { socket } from '../services/socket';

// Helper function to get profile image URL
const getProfileImageUrl = (profilePicture) => {
  if (!profilePicture) return null;
  if (profilePicture.startsWith('http://') || profilePicture.startsWith('https://')) {
    return profilePicture;
  }
  const baseUrl = process.env.REACT_APP_API_URL || 'https://lavender-falcon-985378.hostingersite.com/api/v1';
  const staticBaseUrl = baseUrl.replace('/api/v1', '');
  return `${staticBaseUrl}${profilePicture}`;
};

// ─── Helper: Format full name as "SURNAME, First Middle" ───
const formatFullName = (student) => {
  const surname = student.lastName || '';
  const firstName = student.firstName || '';
  const middleName = student.middleName || '';
  if (!surname && !firstName) return 'N/A';
  const nameParts = [];
  if (surname) nameParts.push(surname + ',');
  if (firstName) nameParts.push(firstName);
  if (middleName) nameParts.push(middleName);
  return nameParts.join(' ');
};

// ─── Helper: Get initials from surname and first name ───
const getInitials = (student) => {
  const surname = student.lastName || '';
  const firstName = student.firstName || '';
  return `${surname.charAt(0) || ''}${firstName.charAt(0) || ''}`.toUpperCase() || 'ST';
};

// Escape values inserted into the printable PDF document.
const escapeHtml = (value) =>
  String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

export default function Students() {
  const { user, hasFeature, getPlanLimit, planAccess } = useAuth();
  const isStarterPlan =
    String(planAccess?.plan?.name || '').trim().toLowerCase() === 'starter';
  const isAdmin = ['admin', 'school_admin', 'super_admin'].includes(user?.role);
  const isTeacher = user?.role === 'teacher';
  const canManageStudents = isAdmin && hasFeature('students');
  const studentLimit = getPlanLimit('students');
  const hasUnlimitedStudents = planAccess?.limits?.unlimited_students === true;

  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterGender, setFilterGender] = useState('all');
  const [filterClass, setFilterClass] = useState('all');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    male: 0,
    female: 0,
  });
  const [uploadingId, setUploadingId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [schoolName, setSchoolName] = useState('School');
  const [uniqueClasses, setUniqueClasses] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingBulk, setDeletingBulk] = useState(false);

  // Refs for background updates
  const fetchStudentsRef = useRef();

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      setIsMobile(mobileRegex.test(userAgent.toLowerCase()));
    };
    checkMobile();
  }, []);

  // Load the tenant's real school name for the PDF heading and watermark.
  useEffect(() => {
    let active = true;

    const fetchSchoolName = async () => {
      const nameFromAuth =
        user?.school?.name ||
        user?.school?.schoolName ||
        user?.school_name ||
        user?.schoolName;

      try {
        const [schoolProfileResponse, currentUserResponse] = await Promise.all([
          apiClient.get('/schools/profile').catch(() => null),
          apiClient.get('/users/me').catch(() => null),
        ]);
        const schoolProfileData =
          schoolProfileResponse?.data?.data ||
          schoolProfileResponse?.data?.school ||
          schoolProfileResponse?.data ||
          {};
        const currentUserData =
          currentUserResponse?.data?.data || currentUserResponse?.data || {};
        const resolvedName =
          schoolProfileData.schoolName ||
          schoolProfileData.name ||
          schoolProfileData.school?.name ||
          schoolProfileData.school?.schoolName ||
          currentUserData.school?.name ||
          currentUserData.school?.schoolName ||
          currentUserData.school_name ||
          currentUserData.schoolName ||
          currentUserData.user?.school?.name ||
          currentUserData.user?.school_name ||
          nameFromAuth;

        if (active && resolvedName) setSchoolName(resolvedName);
      } catch (error) {
        console.error('Error fetching school name:', error);
        if (active && nameFromAuth) setSchoolName(nameFromAuth);
      }
    };

    fetchSchoolName();
    return () => {
      active = false;
    };
  }, [user]);

  // ─── Fetch students with background option ───
  const fetchStudents = async (options = { background: false }) => {
    try {
      if (!options.background) {
        setLoading(true);
      }

      let response;
      if (isAdmin) {
        response = await apiClient.get('/students', {
          params: {
            limit: 1000,
            page: 1
          }
        });
      } else if (isTeacher) {
        try {
          response = await apiClient.get('/teachers/me/students', {
            params: { limit: 1000 }
          });
        } catch (error) {
          console.error('Failed to fetch teacher students:', error);
          response = { data: { data: [] } };
        }
      } else if (user?.role === 'student') {
        response = await apiClient.get('/students', { params: { limit: 1000, page: 1 } });
      } else {
        response = { data: { data: [] } };
      }

      let studentsData = [];
      let totalStudentsCount = 0;
      
      if (response.data) {
        if (Array.isArray(response.data)) {
          studentsData = response.data;
          totalStudentsCount = studentsData.length;
        } else if (response.data.data) {
          studentsData = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
          totalStudentsCount = response.data.pagination?.total || studentsData.length;
        } else if (response.data.rows) {
          studentsData = response.data.rows;
          totalStudentsCount = response.data.count || studentsData.length;
        }
      }
      
      const mappedStudents = studentsData.map(student => ({
        id: student.id,
        firstName: student.first_name || student.firstName,
        lastName: student.last_name || student.lastName,
        middleName: student.middle_name || student.middleName || '',
        email: student.email,
        admissionNumber: student.admission_number || student.admissionNumber,
        classId: student.current_class_id || student.classId,
        class: student.class || null,
        status: student.status || (student.is_active ? 'active' : 'inactive'),
        isActive: student.is_active !== undefined ? student.is_active : student.status === 'active',
        createdAt: student.created_at || student.createdAt,
        user: student.user,
        profilePicture: student.profile_picture || student.profilePicture || student.user?.profile_picture,
        gender: student.gender || 'other',
        phone: student.phone,
        dateOfBirth: student.date_of_birth || student.dateOfBirth,
        nationality: student.nationality,
        religion: student.religion,
        tribe: student.tribe,
        address: student.address,
        city: student.city,
        state: student.state,
        country: student.country,
        enrollmentDate: student.enrollment_date || student.enrollmentDate,
      }));
      
      setStudents(mappedStudents);
      
      const classes = [...new Set(mappedStudents.map(s => s.class?.name).filter(Boolean))].sort();
      setUniqueClasses(classes);
      
      const total = totalStudentsCount;
      const active = mappedStudents.filter(s => s.status === 'active').length;
      const male = mappedStudents.filter(s => s.gender?.toLowerCase() === 'male').length;
      const female = mappedStudents.filter(s => s.gender?.toLowerCase() === 'female').length;
      
      setStats({ total, active, male, female });
    } catch (error) {
      console.error('Error fetching students:', error);
      if (error.response?.status !== 401 && !options.background) {
        await showError({ text: 'Failed to fetch students' });
      }
      if (!options.background) {
        setStats({
          total: 0,
          active: 0,
          male: 0,
          female: 0,
        });
      }
    } finally {
      if (!options.background) {
        setLoading(false);
      }
    }
  };

  // ─── Store fetch function in ref ───
  useEffect(() => {
    fetchStudentsRef.current = fetchStudents;
  }, [fetchStudents]);

  // ─── Initial load ───
  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Socket listener for student updates (background) ───
  useEffect(() => {
    const handleStudentUpdate = (event) => {
      console.log('📡 Student update received:', event);
      if (fetchStudentsRef.current) {
        fetchStudentsRef.current({ background: true });
      }
    };

    socket.on('student:updated', handleStudentUpdate);

    return () => {
      socket.off('student:updated', handleStudentUpdate);
    };
  }, []);

  // ─── Reset selection when filters change ───
  useEffect(() => {
    setSelectedStudents([]);
  }, [filterStatus, filterGender, filterClass, searchTerm]);

  // ─── Handlers ───
  const handleAddStudent = () => {
    if (!canManageStudents) {
      return showWarning({
        title: 'Plan Upgrade Required',
        text: 'Student management is not included in your current school plan.',
      });
    }

    if (!hasUnlimitedStudents && studentLimit !== null && stats.total >= studentLimit) {
      return showWarning({
        title: 'Student Limit Reached',
        text: `Your plan allows up to ${studentLimit} students. Upgrade to enrol another student.`,
      });
    }

    navigate('/app/students/add');
  };
  const handleViewStudent = (id) => navigate(`/app/students/${id}`);
  const handleEditStudent = (id) => navigate(`/app/students/edit/${id}`);

  // ─── Optimistic delete (single) ───
  const handleDeleteStudent = async (id) => {
    if (!isAdmin) return;
    const student = students.find(s => s.id === id);
    if (!student) return;

    const fullName = formatFullName(student);
    const confirmed = await showConfirm({
      title: 'Delete Student',
      text: `Are you sure you want to delete "${fullName}"? This action cannot be undone.`,
      confirmText: 'Yes, delete',
      icon: 'error',
    });
    if (!confirmed) return;

    const previousStudents = [...students];
    setStudents(students.filter(s => s.id !== id));
    setDeletingId(id);

    setStats(prev => ({
      total: prev.total - 1,
      active: student.status === 'active' ? prev.active - 1 : prev.active,
      male: student.gender?.toLowerCase() === 'male' ? prev.male - 1 : prev.male,
      female: student.gender?.toLowerCase() === 'female' ? prev.female - 1 : prev.female,
    }));

    const newFiltered = students.filter(s => s.id !== id);
    const newTotalPages = Math.ceil(newFiltered.length / itemsPerPage);
    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages);
    }

    try {
      await apiClient.delete(`/students/${id}`);
      await showSuccess({ text: 'Student deleted successfully' });
    } catch (error) {
      setStudents(previousStudents);
      setStats(prev => ({
        total: prev.total + 1,
        active: student.status === 'active' ? prev.active + 1 : prev.active,
        male: student.gender?.toLowerCase() === 'male' ? prev.male + 1 : prev.male,
        female: student.gender?.toLowerCase() === 'female' ? prev.female + 1 : prev.female,
      }));
      await showError({ text: error.response?.data?.message || 'Failed to delete student' });
    } finally {
      setDeletingId(null);
    }
  };

  // ─── Optimistic bulk delete ───
  const handleBulkDelete = async () => {
    if (!isAdmin) return;
    if (selectedStudents.length === 0) {
      await showWarning({ title: 'No Selection', text: 'Please select students to delete.' });
      return;
    }

    const confirmed = await showConfirm({
      title: 'Delete Students',
      text: `Are you sure you want to delete ${selectedStudents.length} student(s)? This action cannot be undone.`,
      confirmText: 'Yes, delete all',
      icon: 'error',
    });
    if (!confirmed) return;

    const previousStudents = [...students];
    const idsToDelete = new Set(selectedStudents);
    const studentsToDelete = students.filter(s => idsToDelete.has(s.id));
    const remainingStudents = students.filter(s => !idsToDelete.has(s.id));
    setStudents(remainingStudents);
    setDeletingBulk(true);

    const totalRemoved = studentsToDelete.length;
    const activeRemoved = studentsToDelete.filter(s => s.status === 'active').length;
    const maleRemoved = studentsToDelete.filter(s => s.gender?.toLowerCase() === 'male').length;
    const femaleRemoved = studentsToDelete.filter(s => s.gender?.toLowerCase() === 'female').length;
    setStats(prev => ({
      total: prev.total - totalRemoved,
      active: prev.active - activeRemoved,
      male: prev.male - maleRemoved,
      female: prev.female - femaleRemoved,
    }));
    setSelectedStudents([]);

    const newTotalPages = Math.ceil(remainingStudents.length / itemsPerPage);
    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages);
    }

    try {
      await Promise.all(selectedStudents.map(id => apiClient.delete(`/students/${id}`)));
      await showSuccess({ text: `${selectedStudents.length} students deleted successfully` });
    } catch (error) {
      setStudents(previousStudents);
      setStats(prev => ({
        total: prev.total + totalRemoved,
        active: prev.active + activeRemoved,
        male: prev.male + maleRemoved,
        female: prev.female + femaleRemoved,
      }));
      await showError({ text: error.response?.data?.message || 'Failed to delete some students' });
    } finally {
      setDeletingBulk(false);
    }
  };

  // ─── Profile picture upload ───
  const handleProfilePictureUpload = async (studentId, file) => {
    if (!isAdmin) return;
    if (!file) {
      await showWarning({ title: 'No File', text: 'Please select a file' });
      return;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      await showWarning({ title: 'Invalid File', text: 'Please select a valid image file (JPEG, PNG, GIF, or WEBP)' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      await showWarning({ title: 'File Too Large', text: 'File size must be less than 5MB' });
      return;
    }

    setUploadingId(studentId);
    const formData = new FormData();
    formData.append('profilePicture', file);

    try {
      const response = await apiClient.post(
        `/students/${studentId}/upload-profile-picture`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        await showSuccess({ text: 'Profile picture uploaded successfully!' });
        await fetchStudents();
        setShowUploadModal(false);
        setSelectedFile(null);
        setImagePreview(null);
      } else {
        await showError({ text: response.data.message || 'Failed to upload profile picture' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      await showError({ text: error.response?.data?.message || 'Failed to upload profile picture' });
    } finally {
      setUploadingId(null);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const openUploadModal = (studentId) => {
    if (!isAdmin) return;
    setSelectedStudentId(studentId);
    setShowUploadModal(true);
    setSelectedFile(null);
    setImagePreview(null);
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setSelectedStudentId(null);
    setSelectedFile(null);
    setImagePreview(null);
  };

  // ─── Filtering + Sorting ───
  const filteredStudents = students
    .filter(student => {
      const searchMatch = 
        (student.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (student.lastName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (student.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (student.admissionNumber || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const statusMatch = filterStatus === 'all' || 
        (filterStatus === 'active' && student.status === 'active') ||
        (filterStatus === 'inactive' && student.status === 'inactive');
      
      const genderMatch = filterGender === 'all' || 
        student.gender?.toLowerCase() === filterGender.toLowerCase();
      
      const classMatch = filterClass === 'all' || 
        student.class?.name === filterClass;
      
      return searchMatch && statusMatch && genderMatch && classMatch;
    })
    .sort((a, b) => {
      // Sort by last name (surname) ascending, then first name
      const lastNameA = (a.lastName || '').toUpperCase();
      const lastNameB = (b.lastName || '').toUpperCase();
      if (lastNameA < lastNameB) return -1;
      if (lastNameA > lastNameB) return 1;
      const firstNameA = (a.firstName || '').toUpperCase();
      const firstNameB = (b.firstName || '').toUpperCase();
      if (firstNameA < firstNameB) return -1;
      if (firstNameA > firstNameB) return 1;
      return 0;
    });

  // ─── Print / Export helpers ───
  const getStudentsToPrint = () => {
    if (!isAdmin) return [];
    if (selectedStudents.length > 0) {
      return filteredStudents.filter(s => selectedStudents.includes(s.id));
    }
    return filteredStudents;
  };

  const generateStudentsPrintHTML = () => {
    const safeSchoolName = escapeHtml(schoolName || 'School');
    const printStudents = getStudentsToPrint();
    const sortedStudents = [...printStudents].sort((a, b) => {
      const la = (a.lastName || '').toUpperCase();
      const lb = (b.lastName || '').toUpperCase();
      if (la < lb) return -1;
      if (la > lb) return 1;
      const fa = (a.firstName || '').toUpperCase();
      const fb = (b.firstName || '').toUpperCase();
      if (fa < fb) return -1;
      if (fa > fb) return 1;
      return 0;
    });

    const filterInfo = [];
    if (filterStatus !== 'all') filterInfo.push(`Status: ${filterStatus.toUpperCase()}`);
    if (filterGender !== 'all') filterInfo.push(`Gender: ${filterGender.toUpperCase()}`);
    if (filterClass !== 'all') filterInfo.push(`Class: ${filterClass}`);
    if (searchTerm) filterInfo.push(`Search: "${searchTerm}"`);
    const filterText = filterInfo.length > 0 ? ` (Filtered: ${filterInfo.join(', ')})` : '';

    const totalPrint = sortedStudents.length;

    let tableRows = '';
    sortedStudents.forEach((student, index) => {
      const fullName = escapeHtml(formatFullName(student));
      const initials = escapeHtml(getInitials(student));
      const profileImageUrl = getProfileImageUrl(student.profilePicture);
      const avatarMarkup = profileImageUrl
        ? `<span class="student-avatar student-avatar-photo">
             <img
               src="${escapeHtml(profileImageUrl)}"
               alt="${fullName}"
               onerror="this.parentElement.className='student-avatar student-avatar-initials';this.parentElement.innerHTML='${initials}'"
             />
           </span>`
        : `<span class="student-avatar student-avatar-initials">${initials}</span>`;
      tableRows += `
        <tr>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">${index + 1}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; font-size: 11px;">
            <div style="display:flex;align-items:center;gap:10px;">
              ${avatarMarkup}
              <span>${fullName}</span>
            </div>
          </td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">${student.admissionNumber || 'N/A'}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">${student.gender === 'male' ? 'Male' : student.gender === 'female' ? 'Female' : 'Other'}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">${student.class?.name || 'Not Assigned'}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">
            <span style="display:inline-block;padding:2px 10px;border-radius:3px;font-size:10px;font-weight:600;${student.status === 'active' ? 'background:#d4edda;color:#155724;' : 'background:#fff3cd;color:#856404;'}">
              ${student.status?.toUpperCase() || 'UNKNOWN'}
            </span>
          </td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">
            ${student.createdAt ? new Date(student.createdAt).toLocaleDateString() : 'N/A'}
          </td>
        </tr>
      `;
    });

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${safeSchoolName} - Students List</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Times New Roman', Times, serif; 
              padding: 25px 35px; 
              color: #1a1a1a; 
              background: white;
              font-size: 11px;
            }
            @page {
              size: A4 landscape;
              margin: 10mm 12mm;
            }
            @media print {
              .no-print { display: none !important; }
              body { padding: 15px 20px; }
              th { background: #1a3a5c !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              .status-active { background: #d4edda !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              .status-inactive { background: #fff3cd !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              .student-avatar-initials {
                background: #1a3a5c !important;
                color: #ffffff !important;
                border-color: #1a3a5c !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
            .student-avatar {
              width: 30px;
              height: 30px;
              min-width: 30px;
              min-height: 30px;
              border-radius: 50%;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              flex: 0 0 30px;
              overflow: hidden;
              line-height: 1;
              box-sizing: border-box;
              vertical-align: middle;
            }
            .student-avatar-photo {
              background: #eef2f7;
              border: 1px solid #d7dee8;
            }
            .student-avatar-photo img {
              width: 100%;
              height: 100%;
              display: block;
              object-fit: cover;
              border-radius: 50%;
            }
            .student-avatar-initials {
              background: #1a3a5c;
              border: 1px solid #1a3a5c;
              color: #ffffff;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 10px;
              font-weight: 700;
              letter-spacing: 0.2px;
              text-align: center;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .print-header { 
              display: flex; 
              justify-content: space-between; 
              align-items: flex-start;
              border-bottom: 3px solid #1a3a5c; 
              padding-bottom: 12px; 
              margin-bottom: 15px;
            }
            .print-header-left h1 { 
              font-size: 20px; 
              color: #1a3a5c; 
              font-weight: bold;
              letter-spacing: 1px;
              margin: 0;
            }
            .print-header-left p { 
              color: #555; 
              font-size: 11px;
              margin-top: 3px;
            }
            .print-header-right {
              text-align: right;
              font-size: 10px;
              color: #666;
            }
            .print-header-right p { margin: 2px 0; }
            .print-title {
              text-align: center;
              font-size: 14px;
              font-weight: bold;
              color: #1a3a5c;
              margin-bottom: 12px;
              letter-spacing: 0.5px;
            }
            .student-count-badge {
              display: inline-block;
              background: #1a3a5c;
              color: white;
              padding: 1px 10px;
              border-radius: 12px;
              font-size: 10px;
              font-weight: 600;
            }
            table { 
              width: 100%; 
              border-collapse: collapse;
              margin-top: 5px;
            }
            th { 
              background: #1a3a5c; 
              color: white; 
              padding: 7px 10px; 
              text-align: left; 
              font-size: 10px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              font-weight: 600;
            }
            th:first-child { text-align: center; }
            td { 
              padding: 6px 10px; 
              border-bottom: 1px solid #e8e8e8; 
              font-size: 11px;
              vertical-align: middle;
            }
            tr:nth-child(even) { background: #fafafa; }
            .print-footer { 
              text-align: center; 
              margin-top: 18px; 
              padding-top: 10px; 
              border-top: 1px solid #e0e0e0; 
              color: #888; 
              font-size: 9px;
            }
            .print-footer p { margin-top: 2px; }
            .print-watermark {
              position: fixed;
              bottom: 30px;
              right: 30px;
              opacity: 0.06;
              font-size: 45px;
              color: #1a3a5c;
              font-weight: bold;
              transform: rotate(-20deg);
              pointer-events: none;
            }
            .record-count {
              text-align: right;
              font-size: 10px;
              color: #888;
              margin-top: 8px;
            }
          </style>
        </head>
        <body>
          <div class="print-watermark">${safeSchoolName}</div>

          <div class="print-header">
            <div class="print-header-left">
              <h1>${safeSchoolName}</h1>
              <p>Complete Students List</p>
            </div>
            <div class="print-header-right">
              <p><strong>Generated:</strong> ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              <p><strong>Time:</strong> ${new Date().toLocaleTimeString()}</p>
              <p><strong>Report ID:</strong> #${Math.random().toString(36).substring(2, 8).toUpperCase()}</p>
            </div>
          </div>

          <div class="print-title">
            STUDENTS LIST ${filterText}
            <span class="student-count-badge" style="margin-left:10px;">${totalPrint} Students</span>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width:5%;text-align:center;">#</th>
                <th style="width:25%;">Student Name</th>
                <th style="width:16%;text-align:center;">Admission #</th>
                <th style="width:10%;text-align:center;">Gender</th>
                <th style="width:18%;">Class</th>
                <th style="width:12%;text-align:center;">Status</th>
                <th style="width:14%;text-align:center;">Enrolled</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows || `
                <tr>
                  <td colspan="7" style="text-align:center;padding:30px;color:#999;font-size:14px;">
                    No students found matching the current filters.
                  </td>
                </tr>
              `}
            </tbody>
          </table>

          <div class="record-count">
            Total Records: <strong>${totalPrint}</strong>
          </div>

          <div class="print-footer">
            <p>This is a computer-generated document. No signature required.</p>
            <p>${new Date().getFullYear()} ${safeSchoolName} - All Rights Reserved</p>
            <p style="font-size:8px;color:#bbb;margin-top:3px;">Confidential - For authorized personnel only</p>
          </div>
        </body>
      </html>
    `;
  };

  const handlePrint = () => {
    if (isStarterPlan) {
      return showWarning({
        title: 'PDF Download Not Available',
        text: 'PDF downloads are not included in the Starter plan. Upgrade to Basic or a higher plan to download PDFs.',
      });
    }

    if (!isAdmin) return;
    const studentsToPrint = getStudentsToPrint();
    if (studentsToPrint.length === 0) {
      showWarning({ title: 'No Data', text: 'No students to print. Please adjust your filters.' });
      return;
    }
    
    setIsPrinting(true);
    
    try {
      const printWindow = window.open('', '_blank', 'width=1100,height=800');
      if (printWindow) {
        printWindow.document.write(generateStudentsPrintHTML());
        printWindow.document.close();
        printWindow.document.title = `${schoolName || 'School'} - Students List`;
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
    } catch (error) {
      console.error('Print error:', error);
      showError({ text: 'Failed to generate print preview' });
    } finally {
      setIsPrinting(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterGender('all');
    setFilterClass('all');
    setSelectedStudents([]);
    setCurrentPage(1);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filterStatus !== 'all') count++;
    if (filterGender !== 'all') count++;
    if (filterClass !== 'all') count++;
    if (searchTerm) count++;
    return count;
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredStudents.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);

  if (loading) {
    return <FireLoader fullScreen={true} size="lg" text="LOADING..." />;
  }

  return (
    <div className="space-y-5 sm:space-y-6 px-4 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-primary-700">Students</h1>
          <p className="text-sm sm:text-base text-primary-400 mt-0.5 sm:mt-1">
            {canManageStudents ? 'Manage all students and their information' : 'View school student records'}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {canManageStudents && selectedStudents.length > 0 && (
            <button
              onClick={handleBulkDelete}
              disabled={deletingBulk}
              className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 text-sm"
            >
              {deletingBulk ? (
                <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5 sm:mr-2" />
              ) : (
                <TrashIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
              )}
              <span className="hidden xs:inline">Delete Selected</span>
              <span className="xs:hidden">Delete</span>
              <span className="ml-1">({selectedStudents.length})</span>
            </button>
          )}
          {isAdmin && filteredStudents.length > 0 && (
            <button
              onClick={handlePrint}
              disabled={isPrinting}
              className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
            >
              <PrinterIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
              <span>{isPrinting ? 'Preparing PDF...' : 'PDF'}</span>
            </button>
          )}
          {isAdmin && (
            <button 
              onClick={handleAddStudent}
              className={`inline-flex items-center px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg transition-colors text-sm ${canManageStudents && (hasUnlimitedStudents || studentLimit === null || stats.total < studentLimit) ? 'bg-primary-600 text-white hover:bg-primary-700' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}
            >
              <UserPlusIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
              <span>{!canManageStudents ? 'Upgrade to Add' : (!hasUnlimitedStudents && studentLimit !== null && stats.total >= studentLimit) ? 'Limit Reached' : 'Add Student'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards – flattened */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white rounded-lg p-3 sm:p-5 border border-gray-100 hover:border-primary-200 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-primary-400">Total Students</p>
              <p className="text-xl sm:text-3xl font-bold text-primary-700 mt-0.5">{stats.total}</p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
              <UserGroupIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
          </div>
          <div className="mt-2 pt-2 border-t border-gray-50">
            <p className="text-[10px] sm:text-xs text-primary-400">
              {isAdmin ? 'Total enrolled students' : 'Students in your classes'}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-3 sm:p-5 border border-gray-100 hover:border-green-200 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-primary-400">Active Students</p>
              <p className="text-xl sm:text-3xl font-bold text-green-600 mt-0.5">{stats.active}</p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
              <AcademicCapIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
          </div>
          <div className="mt-2 pt-2 border-t border-gray-50">
            <p className="text-[10px] sm:text-xs text-primary-400">{stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0}% of total</p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-3 sm:p-5 border border-gray-100 hover:border-blue-200 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-primary-400">Boys</p>
              <p className="text-xl sm:text-3xl font-bold text-blue-600 mt-0.5">{stats.male}</p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>
          <div className="mt-2 pt-2 border-t border-gray-50">
            <p className="text-[10px] sm:text-xs text-primary-400">{stats.total > 0 ? Math.round((stats.male / stats.total) * 100) : 0}% of total</p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-3 sm:p-5 border border-gray-100 hover:border-pink-200 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-primary-400">Girls</p>
              <p className="text-xl sm:text-3xl font-bold text-pink-600 mt-0.5">{stats.female}</p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-pink-100 text-pink-600 flex items-center justify-center">
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>
          <div className="mt-2 pt-2 border-t border-gray-50">
            <p className="text-[10px] sm:text-xs text-primary-400">{stats.total > 0 ? Math.round((stats.female / stats.total) * 100) : 0}% of total</p>
          </div>
        </div>
      </div>

      {/* Search and Filter – flattened */}
      <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-100">
        <div className="flex flex-col lg:flex-row gap-3 sm:gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-primary-400" />
            <input
              type="text"
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 placeholder-primary-400 text-sm transition-colors"
            />
          </div>
          
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="flex-1 min-w-[100px] px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm transition-colors"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              value={filterGender}
              onChange={(e) => setFilterGender(e.target.value)}
              className="flex-1 min-w-[100px] px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm transition-colors"
            >
              <option value="all">All Genders</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>

            <select
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
              className="flex-1 min-w-[120px] px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm transition-colors"
            >
              <option value="all">All Classes</option>
              {uniqueClasses.map((className, index) => (
                <option key={index} value={className}>{className}</option>
              ))}
            </select>

            {getActiveFilterCount() > 0 && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 sm:px-4 sm:py-2.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1.5 sm:gap-2 text-sm"
              >
                <XMarkIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden xs:inline">Clear Filters</span>
                <span className="xs:hidden">Clear</span>
                <span className="text-xs">({getActiveFilterCount()})</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Students Table – with updated name format and sorting */}
      <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle px-4 sm:px-0">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50/50">
                <tr>
                  {canManageStudents && (
                    <th className="px-2 sm:px-4 py-3 text-left w-10">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStudents(filteredStudents.map(student => student.id));
                          } else {
                            setSelectedStudents([]);
                          }
                        }}
                        checked={selectedStudents.length === filteredStudents.length && filteredStudents.length > 0}
                        className="w-4 h-4 text-primary-500 rounded border-gray-300 focus:ring-primary-200"
                      />
                    </th>
                  )}
                  <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-primary-600">Student</th>
                  <th className="hidden sm:table-cell px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-primary-600">Admission #</th>
                  <th className="hidden md:table-cell px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-primary-600">Gender</th>
                  <th className="hidden lg:table-cell px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-primary-600">Class</th>
                  <th className="hidden sm:table-cell px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-primary-600">Status</th>
                  <th className="hidden lg:table-cell px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-primary-600">Enrolled</th>
                  <th className="px-3 sm:px-6 py-3 text-right text-xs sm:text-sm font-semibold text-primary-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {currentItems.map((student) => {
                  const profileImageUrl = getProfileImageUrl(student.profilePicture);
                  const fullName = formatFullName(student);
                  const initials = getInitials(student);
                  const isDeleting = deletingId === student.id;
                  
                  return (
                    <tr key={student.id} className={`hover:bg-gray-50/50 transition-colors group ${isDeleting ? 'opacity-50' : ''}`}>
                      {canManageStudents && (
                        <td className="px-2 sm:px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedStudents.includes(student.id)}
                            onChange={() => {
                              if (selectedStudents.includes(student.id)) {
                                setSelectedStudents(selectedStudents.filter(sid => sid !== student.id));
                              } else {
                                setSelectedStudents([...selectedStudents, student.id]);
                              }
                            }}
                            className="w-4 h-4 text-primary-500 rounded border-gray-300 focus:ring-primary-200"
                            disabled={isDeleting}
                          />
                        </td>
                      )}
                      <td className="px-3 sm:px-6 py-3">
                        <div 
                          className="flex items-center space-x-2 sm:space-x-3 cursor-pointer group"
                          onClick={() => handleViewStudent(student.id)}
                        >
                          <div className="relative">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold text-xs sm:text-sm flex-shrink-0 overflow-hidden group-hover:ring-2 group-hover:ring-primary-400 transition-all">
                              {profileImageUrl ? (
                                <img 
                                  src={profileImageUrl} 
                                  alt={fullName}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.textContent = initials;
                                  }}
                                />
                              ) : (
                                <span>{initials}</span>
                              )}
                            </div>
                            {canManageStudents && (
                              <label
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isMobile) {
                                    const input = document.getElementById(`upload-${student.id}`);
                                    if (input) input.click();
                                  } else {
                                    openUploadModal(student.id);
                                  }
                                }}
                                className="absolute -bottom-1 -right-1 p-1 bg-white rounded-full shadow-sm hover:shadow-md transition-all group-hover:scale-110 cursor-pointer"
                                title={isMobile ? "Take photo or select from gallery" : "Upload profile picture"}
                              >
                                <CameraIcon className="w-3 h-3 sm:w-4 sm:h-4 text-primary-500" />
                                {isMobile ? (
                                  <input
                                    id={`upload-${student.id}`}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      const file = e.target.files[0];
                                      if (file) {
                                        handleProfilePictureUpload(student.id, file);
                                      }
                                      e.target.value = '';
                                    }}
                                    className="hidden"
                                  />
                                ) : (
                                  <input
                                    id={`upload-${student.id}`}
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      const file = e.target.files[0];
                                      if (file) {
                                        handleProfilePictureUpload(student.id, file);
                                      }
                                      e.target.value = '';
                                    }}
                                    className="hidden"
                                  />
                                )}
                              </label>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-primary-700 text-sm sm:text-base truncate group-hover:text-primary-600 transition-colors">
                              {fullName}
                            </p>
                            <p className="text-xs sm:text-sm text-primary-400 truncate">{student.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="hidden sm:table-cell px-3 sm:px-6 py-3 text-xs sm:text-sm text-primary-600 font-medium whitespace-nowrap">
                        {student.admissionNumber || 'N/A'}
                      </td>
                      <td className="hidden md:table-cell px-3 sm:px-6 py-3">
                        <span className={`inline-flex px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full whitespace-nowrap ${
                          student.gender?.toLowerCase() === 'male' 
                            ? 'bg-blue-100 text-blue-700' 
                            : student.gender?.toLowerCase() === 'female'
                            ? 'bg-pink-100 text-pink-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {student.gender ? student.gender.toUpperCase() : 'N/A'}
                        </span>
                      </td>
                      <td className="hidden lg:table-cell px-3 sm:px-6 py-3 text-xs sm:text-sm text-primary-600 whitespace-nowrap">
                        {student.class?.name || 'Not Assigned'}
                      </td>
                      <td className="hidden sm:table-cell px-3 sm:px-6 py-3">
                        <span className={`inline-flex px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full whitespace-nowrap ${
                          student.status === 'active' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {student.status || (student.isActive ? 'active' : 'inactive')}
                        </span>
                      </td>
                      <td className="hidden lg:table-cell px-3 sm:px-6 py-3 text-xs sm:text-sm text-primary-500 whitespace-nowrap">
                        {student.createdAt ? new Date(student.createdAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-3 sm:px-6 py-3 text-right">
                        <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                          <button 
                            onClick={() => handleViewStudent(student.id)}
                            className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 text-primary-400 hover:text-primary-600 transition-colors"
                            title="View Student"
                            disabled={isDeleting}
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          {canManageStudents && (
                            <>
                              <button 
                                onClick={() => handleEditStudent(student.id)}
                                className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 text-primary-400 hover:text-primary-600 transition-colors"
                                title="Edit Student"
                                disabled={isDeleting}
                              >
                                <PencilSquareIcon className="w-4 h-4" />
                              </button>
                              <button 
                                onClick={() => handleDeleteStudent(student.id)}
                                disabled={isDeleting}
                                className="p-1.5 sm:p-2 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors disabled:opacity-50"
                                title="Delete Student"
                              >
                                {isDeleting ? (
                                  <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <TrashIcon className="w-4 h-4" />
                                )}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        
        {filteredStudents.length === 0 && (
          <div className="text-center py-8 sm:py-12">
            <UserGroupIcon className="w-12 h-12 sm:w-16 sm:h-16 text-gray-200 mx-auto mb-3 sm:mb-4" />
            <p className="text-sm sm:text-base text-primary-400">No students found matching the current filters</p>
            <button 
              onClick={clearFilters}
              className="mt-3 sm:mt-4 inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
            >
              <XMarkIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
              Clear Filters
            </button>
          </div>
        )}

        {/* Pagination – flattened */}
        {filteredStudents.length > 0 && (
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <p className="text-xs sm:text-sm text-primary-400 text-center sm:text-left">
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredStudents.length)} of {filteredStudents.length} students
            </p>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-1.5 sm:p-2 rounded-lg border border-gray-200 text-primary-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-2.5 py-1 sm:px-4 sm:py-2 rounded-lg transition-colors text-sm ${
                      currentPage === pageNum
                        ? 'bg-primary-600 text-white'
                        : 'text-primary-600 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="p-1.5 sm:p-2 rounded-lg border border-gray-200 text-primary-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRightIcon className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Upload Profile Picture Modal – flattened */}
      {canManageStudents && !isMobile && showUploadModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-lg max-w-md w-full p-4 sm:p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-primary-700">Upload Profile Picture</h3>
              <button
                onClick={closeUploadModal}
                className="p-1 rounded-lg hover:bg-gray-100 text-primary-400 hover:text-primary-600 transition-colors"
              >
                <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border-2 border-gray-200">
                  {imagePreview ? (
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <CameraIcon className="w-10 h-10 sm:w-12 sm:h-12 text-primary-400" />
                  )}
                </div>
              </div>

              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="w-full py-2 sm:py-3 px-3 sm:px-4 border-2 border-dashed border-gray-200 rounded-lg text-center hover:border-primary-400 transition-colors text-sm">
                  <p className="text-primary-500 truncate">
                    {selectedFile ? selectedFile.name : 'Click or drag to select an image'}
                  </p>
                  <p className="text-xs text-primary-400 mt-1">Max size: 5MB (JPG, PNG, GIF, WEBP)</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  onClick={closeUploadModal}
                  className="flex-1 py-2 px-4 border border-gray-200 text-primary-600 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleProfilePictureUpload(selectedStudentId, selectedFile)}
                  disabled={!selectedFile || uploadingId === selectedStudentId}
                  className="flex-1 py-2 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {uploadingId === selectedStudentId ? (
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Uploading...
                    </div>
                  ) : (
                    'Upload'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}